import * as React from 'react';
import Link from '@mui/material/Link';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { useAuth, useAccount } from '../../auth';
import { useEntities } from '../../data/flowEntities';
import { useCatalogType } from '../../data/catalog';
import { useSupportCase } from '../../data/support/useSupportCustomer';
import { getEntityType, getEntityIcon } from '../../utils/entities';
import Typography from '@mui/material/Typography';
import {
  LinkRouterProps,
  BreadcrumbItemProps,
  BreadcrumbMenuProps,
  MainMenuProps,
  EntitiesMenuProps,
  CatalogItemProps,
  BreadcrumbMenuItem,
} from '../../types/breadcrumb.types';
import { capitalize } from '@mui/material';
import { isValidRoutePath } from '../../utils/routes-utls';

export const LinkRouter: React.FC<LinkRouterProps> = (props) => (
  <Link {...props} component={RouterLink} />
);

export const BreadcrumbItem: React.FC<BreadcrumbItemProps> = (props) => {
  // Determine the link destination
  let to = props.to || '';
  // If children is 'Syncs', replace '/syncs' at the end of the link with '?tab=syncs'
  const childrenText =
    typeof props.children === 'string' ? props.children : undefined;
  if (childrenText === 'Syncs' && to.endsWith('/syncs')) {
    to = to.replace(/\/syncs$/, '?tab=syncs');
  }
  return props.isLast || !props.useLink ? (
    <span
      style={{ color: '#27347B', fontFamily: 'Noto Sans', fontSize: '14px' }}
    >
      {props.children || props.title || props.name}
    </span>
  ) : (
    <LinkRouter
      style={{
        color: '#27347B',
        textDecorationColor: '#27347B',
        fontFamily: 'Noto Sans',
        fontSize: '10pt',
      }}
      to={to}
    >
      {props.children || props.title || props.name}
    </LinkRouter>
  );
};

export const BreadcrumbMenu: React.FC<BreadcrumbMenuProps> = (props) => {
  const { currentAccount } = useAccount();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const ancherRef = React.useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  const handleTextClick = () => {
    if (props.to && !props.isLast) {
      navigate(props.to);
    } else {
      setAnchorEl(ancherRef.current);
    }
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(ancherRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (item: BreadcrumbMenuItem) => {
    handleClose();
    if (item.onSelect) {
      item.onSelect();
    } else if (item.path) {
      navigate(`/${currentAccount.name}/${item.path}`);
    }
  };

  const buttonId = 'breadcrumb-menu-button' + props.to?.replace('/', '-');
  const menuId = 'breadcrumb-menu' + props.to?.replace('/', '-');

  return (
    <>
      <div
        ref={ancherRef}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          onClick={handleTextClick}
          sx={{
            color: (theme) => theme.palette.text.primary,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            ...props.triggerButtonSX,
          }}
        >
          {props.title || props.name}
        </Typography>
        <ArrowDropDownIcon
          onClick={handleArrowClick}
          sx={{
            fontSize: '20px',
            color: '#27347B',
            cursor: 'pointer',
            ml: '-2px',
          }}
        />
      </div>

      <Menu
        id={menuId}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={props.menuSX}
      >
        {props.items.map((value, index) => {
          if (value.name === props.selectedName) return null;
          return (
            <MenuItem
              onClick={() => handleSelect(value)}
              key={value.key || value.path}
              disableRipple
            >
              {value.icon &&
                React.createElement(value.icon, {
                  style: {
                    padding: '0.25em',
                    paddingRight: '0.5em',
                  },
                })}
              {value.title || value.name}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// TODO: how to generate this list from the routes?
export const items = [
  {
    title: 'Connections',
    name: 'connections',
    path: 'connections',
    icon: getEntityIcon('integrations'),
  },
  {
    title: 'Datastores',
    name: 'datastores',
    path: 'datastores',
    icon: getEntityIcon('datastores'),
  },
  {
    title: 'Blobstores',
    name: 'blobstores',
    path: 'blobstores',
    icon: getEntityIcon('blobstores'),
  },
  {
    title: 'Processors',
    name: 'processors',
    path: 'processors',
    icon: getEntityIcon('processors'),
  },
  {
    title: 'Flows',
    name: 'flows',
    path: 'flows',
    icon: getEntityIcon('flows'),
  },
];

const MainMenu: React.FC<MainMenuProps> = (props) => {
  return <BreadcrumbMenu {...props} items={items} />;
};

const EntitiesMenu: React.FC<EntitiesMenuProps> = (props) => {
  const { currentAccount } = useAccount();
  const entitiesQuery = useEntities(currentAccount.id, props.entityType);

  const items: BreadcrumbMenuItem[] = [];
  if (entitiesQuery.data) {
    const entities = Array.isArray(entitiesQuery.data)
      ? entitiesQuery.data
      : [entitiesQuery.data];

    for (const entity of entities) {
      if ('name' in entity && entity.name === props.name) continue;
      items.push({
        name: 'name' in entity ? entity.name : '',
        path: `${props.to}/${'name' in entity ? entity.name : ''}`,
      });
    }
  }

  if (items.length > 0) {
    return <BreadcrumbMenu {...props} items={items} />;
  } else {
    return <BreadcrumbItem {...props} useLink={props.useLink} />;
  }
};

const CatalogItem: React.FC<CatalogItemProps> = ({
  name,
  index,
  pathnames,
  isLast,
  useLink,
}) => {
  const { currentAccount } = useAccount();
  const catalogType = useCatalogType(currentAccount.id, pathnames[index - 1]);

  if (catalogType.data && 'title' in catalogType.data) {
    return (
      <BreadcrumbItem
        to={`/${currentAccount.name}/${pathnames[index - 1]}/catalog/${name}`}
        isLast={isLast}
        useLink={useLink}
      >
        {String(catalogType.data['title'])}
      </BreadcrumbItem>
    );
  } else {
    return (
      <BreadcrumbItem isLast={isLast} useLink={useLink}>
        {name}
      </BreadcrumbItem>
    );
  }
};

const SupportCaseBreadcrumbItem: React.FC<{
  caseId: string;
  to: string;
  isLast: boolean;
  useLink: boolean;
}> = ({ caseId, to, isLast, useLink }) => {
  const { currentAccount } = useAccount();
  const caseQuery = useSupportCase(caseId, currentAccount?.id);
  const label = caseQuery.data?.subject || caseId;
  return (
    <BreadcrumbItem to={to} isLast={isLast} useLink={useLink}>
      {label}
    </BreadcrumbItem>
  );
};

// TODO: how to generate this list from the routes?
export const breadcrumbMap = {
  connections: {
    title: 'Connections',
    name: 'connections',
    path: 'connections',
    component: MainMenu,
    child: {
      component: EntitiesMenu,
      props: {
        entityType: 'integration',
      },
    },
  },
  datastores: {
    title: 'Datastores',
    name: 'datastores',
    path: 'datastores',
    component: MainMenu,
    child: {
      component: EntitiesMenu,
      props: {
        entityType: 'datastore',
      },
    },
  },
  blobstores: {
    title: 'Blobstores',
    name: 'blobstores',
    path: 'blobstores',
    component: MainMenu,
    child: {
      component: EntitiesMenu,
      props: {
        entityType: 'blobstore',
      },
    },
  },
  processors: {
    title: 'Processors',
    name: 'processors',
    path: 'processors',
    component: MainMenu,
    child: {
      component: EntitiesMenu,
      props: {
        entityType: 'processor',
      },
    },
  },
  connect: {
    title: 'Connect',
    child: {
      component: CatalogItem,
    },
  },
  configure: {
    title: 'Configure',
    child: {
      component: CatalogItem,
    },
  },
  flows: {
    title: 'Flows',
    name: 'flows',
    path: 'flows',
    component: MainMenu,
  },
};

export const BreadcrumbNav = () => {
  const { currentAccount } = useAccount();
  const location = useLocation();
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x)
    .filter((x) => x != currentAccount.name);

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="breadcrumb"
    >
      {pathnames.map((value, index) => {
        const last = index === pathnames.length - 1;
        const to = `/${currentAccount.name}/${pathnames.slice(0, index + 1).join('/')}`;
        const useLink = isValidRoutePath(to);

        if (index === 0) {
          return (
            <MainMenu
              key={to}
              to={to}
              title={capitalize(value)}
              isLast={last}
              selectedName={value}
              useLink={useLink}
            />
          );
        }

        if (index === 1) {
          const entityType = getEntityType(value);
          if (entityType) {
            return (
              <EntitiesMenu
                key={to}
                to={to}
                title={value}
                isLast={last}
                entityType={entityType}
                name={value}
                useLink={useLink}
              />
            );
          }
        }

        if (index === 2 && pathnames[1] === 'catalog') {
          return (
            <CatalogItem
              key={to}
              name={value}
              index={index}
              pathnames={pathnames}
              isLast={last}
              useLink={useLink}
            />
          );
        }

        if (index === 1 && pathnames[0] === 'support') {
          return (
            <SupportCaseBreadcrumbItem
              key={to}
              caseId={value}
              to={to}
              isLast={last}
              useLink={useLink}
            />
          );
        }

        return (
          <BreadcrumbItem key={to} to={to} isLast={last} useLink={useLink}>
            {index === 1 ? value : capitalize(value)}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumbs>
  );
};
